import { z } from 'zod'
import { Currency } from '../../enums/currency.enum'
import { PaymentType } from '../../enums/payment-type.enum'

// base shape without refinements; the update schema calls .partial() on this, so it
// must stay a plain ZodObject (a refined schema cannot be partialed).
// amount and spentAmount allow negatives (a negative expense records a refund of an
// already-settled expense) but reject zero. conversionRate stays strictly positive —
// it is a magnitude ratio, and for a refund the service derives it from spentAmount /
// amount where both share the same sign, so the quotient is always positive.
export const createExpenseBaseSchema = z.object({
  amount: z.number().refine((v) => v !== 0, 'amount must not be zero'),
  currency: z.nativeEnum(Currency),
  description: z.string().max(500).optional(),
  date: z
    .string()
    .datetime({ offset: true })
    .optional()
    .default(() => new Date().toISOString()),
  paymentType: z.nativeEnum(PaymentType),
  accountId: z.string().guid(),
  categoryId: z.string().guid(),
  spentAmount: z
    .number()
    .refine((v) => v !== 0, 'spentAmount must not be zero')
    .optional(),
  conversionRate: z.number().positive().optional(),
  groupId: z.string().guid().nullish(),
  // idempotency key minted by the offline client before it queues the expense. replaying
  // the same key returns the row created by the first attempt rather than inserting again,
  // which is what makes a queued write safe to retry after an ambiguous network failure.
  clientRequestId: z.string().guid().optional(),
})

// a cross-currency expense deducts spentAmount (account currency) for amount (expense
// currency); the two must share a sign so the derived conversion rate stays positive and
// the balance moves the right way — a refund (negative amount) must return money, not take it.
export const sameSignRefinement = (d: { amount?: number; spentAmount?: number }): boolean =>
  d.amount === undefined ||
  d.spentAmount === undefined ||
  Math.sign(d.amount) === Math.sign(d.spentAmount)

export const sameSignError = {
  message: 'spentAmount must have the same sign as amount',
  path: ['spentAmount'] as PropertyKey[],
}

export const createExpenseSchema = createExpenseBaseSchema.refine(sameSignRefinement, sameSignError)

export type CreateExpenseDto = z.infer<typeof createExpenseSchema>
